import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  THead,
  TBody,
  Th,
  Tr,
  Td,
  Badge,
  Spinner,
  Text,
} from "@twilio-paste/core";
import { PlusIcon } from "@twilio-paste/icons/esm/PlusIcon";
import { EditIcon } from "@twilio-paste/icons/esm/EditIcon";
import { OperatorDto } from "../../dto/postLogin";
import { TeamDto } from "../../dto/team";
import { RolesDto } from "../../dto/roles";
import {
  fetchAllOperators,
  fetchTeams,
  fetchRoles,
  updateOperator,
} from "../../api/adminApi";
import OperatorFormModal from "./OperatorFormModal";

const OperatorsPanel: React.FC = () => {
  const [operators, setOperators] = useState<OperatorDto[]>([]);
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [roles, setRoles] = useState<RolesDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState<OperatorDto | null>(
    null
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [ops, ts, rs] = await Promise.all([
          fetchAllOperators(),
          fetchTeams(),
          fetchRoles(),
        ]);
        setOperators(ops);
        setTeams(ts);
        setRoles(rs);
      } catch (e: any) {
        setError("Errore nel caricamento degli operatori.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const handleNewOperator = () => {
    setEditingOperator(null);
    setModalOpen(true);
  };

  const handleEditOperator = (op: OperatorDto) => {
    setEditingOperator(op);
    setModalOpen(true);
  };

  const handleToggleActive = async (op: OperatorDto) => {
    try {
      const updated = await updateOperator(op.id, {
        name: op.name,
        teamId: op.teamId,
        roleId: op.roleId,
        isActive: !op.isActive,
      });
      setOperators((prev) =>
        prev.map((o) => (o.id === updated.id ? updated : o))
      );
    } catch {
      setError("Errore durante l'aggiornamento dell'operatore.");
    }
  };

  const handleSaved = (saved: OperatorDto) => {
    setOperators((prev) => {
      const exists = prev.find((o) => o.id === saved.id);
      if (exists) return prev.map((o) => (o.id === saved.id ? saved : o));
      return [...prev, saved];
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" padding="space80">
        <Spinner decorative={false} title="Caricamento operatori..." />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="space50"
      >
        <Text as="h4" fontSize="fontSize40" fontWeight="fontWeightBold">
          Operatori ({operators.length})
        </Text>
        <Button variant="primary" onClick={handleNewOperator}>
          <PlusIcon decorative />
          Nuovo operatore
        </Button>
      </Box>

      {error && (
        <Box marginBottom="space40">
          <Text as="p" color="colorTextError">
            {error}
          </Text>
        </Box>
      )}

      <Table>
        <THead>
          <Tr>
            <Th>Nome</Th>
            <Th>Email</Th>
            <Th>Team</Th>
            <Th>Ruolo</Th>
            <Th>Stato</Th>
            <Th>Azioni</Th>
          </Tr>
        </THead>
        <TBody>
          {operators.map((op) => (
            <Tr key={op.id}>
              <Td>{op.name}</Td>
              <Td>{op.email}</Td>
              <Td>{op.teamName}</Td>
              <Td>{op.roleName}</Td>
              <Td>
                <Badge as="span" variant={op.isActive ? "success" : "neutral"}>
                  {op.isActive ? "Attivo" : "Disabilitato"}
                </Badge>
              </Td>
              <Td>
                <Box display="flex" columnGap="space30">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => handleEditOperator(op)}
                  >
                    <EditIcon decorative />
                    Modifica
                  </Button>
                  <Button
                    variant={
                      op.isActive ? "destructive_secondary" : "secondary"
                    }
                    size="small"
                    onClick={() => handleToggleActive(op)}
                  >
                    {op.isActive ? "Disabilita" : "Abilita"}
                  </Button>
                </Box>
              </Td>
            </Tr>
          ))}
          {operators.length === 0 && (
            <Tr>
              <Td colSpan={6}>
                <Text as="p" color="colorTextWeak" textAlign="center">
                  Nessun operatore trovato.
                </Text>
              </Td>
            </Tr>
          )}
        </TBody>
      </Table>

      <OperatorFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
        operator={editingOperator}
        teams={teams}
        roles={roles}
      />
    </Box>
  );
};

export default OperatorsPanel;
